import type {
  AnalyticsDevice,
  AnalyticsErrorCategory,
  AnalyticsLanguage,
  AnalyticsNavigationKind,
  AnalyticsPageKey,
} from '@omaplus/shared-types';

import {
  type BrowserAttribution,
  type BrowserSourceInput,
  classifyBrowserAttribution,
} from './browserAttribution.js';
import type { CaptureController } from './captureController.js';

export { type BrowserSourceInput, classifyBrowserAttribution } from './browserAttribution.js';

export interface BrowserNavigationStart {
  readonly navigationId: string;
  readonly navigationKind: AnalyticsNavigationKind;
  /** Validated from PerformanceNavigationTiming by the mounted browser owner. */
  readonly documentLoadDurationMs?: number;
}

export interface BrowserDisplayedPage {
  readonly navigationId: string;
  readonly pageKey: AnalyticsPageKey;
  /** Current selected interface language, classified at display time. */
  readonly language?: string | null;
}

export interface BrowserCapture {
  navigationStarted(navigation: BrowserNavigationStart): void;
  documentLoadObserved(durationMs: number): void;
  pageNoLongerDisplayed(navigationId: string): void;
  pageDisplayed(page: BrowserDisplayedPage): void;
  pageHidden(): void;
  errorObserved(category: AnalyticsErrorCategory): void;
  visibilityChanged(state: Readonly<{ visible: boolean; focused: boolean }>): void;
  tick(): void;
  accountGenerationChanged(): void;
  dispose(): void;
}

export interface CreateBrowserCaptureOptions {
  readonly controller: CaptureController;
  readonly now: () => number;
  readonly userAgent: string;
  readonly language: string | null | undefined;
  /** Raw attribution is classified once and then discarded. */
  readonly entry: BrowserSourceInput;
}

interface NavigationCandidate {
  readonly navigationKind: AnalyticsNavigationKind;
  readonly startedAt: number;
  readonly documentLoadDurationMs?: number;
}

export function classifyBrowserDevice(userAgent: string): AnalyticsDevice {
  const normalized = userAgent.toLowerCase();
  const mobile = /iphone|ipod|android.+mobile/.test(normalized);
  const tablet = /ipad|tablet|android/.test(normalized) && !/android.+mobile/.test(normalized);
  const desktop = /windows nt|macintosh|x11|linux x86_64/.test(normalized);
  const classifications = Number(mobile) + Number(tablet) + Number(desktop);
  if (classifications !== 1) {
    return 'unknown';
  }
  if (mobile) {
    return 'mobile';
  }
  if (tablet) {
    return 'tablet';
  }
  return 'desktop';
}

export function classifyBrowserLanguage(language: string | null | undefined): AnalyticsLanguage {
  if (language === 'fi' || language === 'sv' || language === 'en') {
    return language;
  }
  return 'unknown';
}

class BrowserCaptureImpl implements BrowserCapture {
  readonly #controller: CaptureController;
  readonly #now: () => number;
  readonly #device: AnalyticsDevice;
  readonly #language: AnalyticsLanguage;
  #attribution: BrowserAttribution;
  readonly #candidates = new Map<string, NavigationCandidate>();

  constructor({ controller, now, userAgent, language, entry }: CreateBrowserCaptureOptions) {
    this.#controller = controller;
    this.#now = now;
    this.#device = classifyBrowserDevice(userAgent);
    this.#language = classifyBrowserLanguage(language);
    this.#attribution = classifyBrowserAttribution(entry);
  }

  navigationStarted({
    navigationId,
    navigationKind,
    documentLoadDurationMs,
  }: BrowserNavigationStart): void {
    if (this.#candidates.has(navigationId)) {
      return;
    }
    // The mounted router has one foreground transition. A route that never
    // reports successful readiness is replaced by the next transition rather
    // than retained as an unbounded prefetch-like candidate.
    this.#candidates.clear();
    this.#candidates.set(navigationId, {
      navigationKind,
      startedAt: this.#now(),
      ...(documentLoadDurationMs === undefined ? {} : { documentLoadDurationMs }),
    });
    this.#controller.navigationStarted(navigationId);
  }

  documentLoadObserved(durationMs: number): void {
    this.#controller.documentLoadObserved(durationMs);
  }

  pageNoLongerDisplayed(navigationId: string): void {
    this.#controller.pageNoLongerDisplayed(navigationId);
  }

  pageDisplayed({ navigationId, pageKey, language }: BrowserDisplayedPage): void {
    const candidate = this.#candidates.get(navigationId);
    if (candidate === undefined) {
      return;
    }
    this.#candidates.delete(navigationId);
    const durationMs =
      candidate.navigationKind === 'document'
        ? candidate.documentLoadDurationMs
        : this.#now() - candidate.startedAt;
    const load =
      candidate.navigationKind === 'history' ||
      durationMs === undefined ||
      !Number.isFinite(durationMs) ||
      durationMs < 0
        ? undefined
        : { kind: candidate.navigationKind, durationMs };
    this.#controller.pageDisplayed({
      navigationId,
      pageKey,
      navigationKind: candidate.navigationKind,
      device: this.#device,
      source: this.#attribution.source,
      referrerHost: this.#attribution.referrerHost,
      campaignSource: this.#attribution.campaignSource,
      campaignMedium: this.#attribution.campaignMedium,
      campaignName: this.#attribution.campaignName,
      language: language === undefined ? this.#language : classifyBrowserLanguage(language),
      ...(load === undefined ? {} : { load }),
    });
  }

  pageHidden(): void {
    this.#controller.pageHidden();
  }

  errorObserved(category: AnalyticsErrorCategory): void {
    this.#controller.errorObserved(category);
  }

  visibilityChanged(state: Readonly<{ visible: boolean; focused: boolean }>): void {
    this.#controller.visibilityChanged(state);
  }

  tick(): void {
    this.#controller.tick();
  }

  accountGenerationChanged(): void {
    this.#candidates.clear();
    // Entry attribution belongs to the member generation that first received
    // it. A later member generation must not inherit browser entry facts.
    this.#attribution = {
      source: 'unknown',
      referrerHost: null,
      campaignSource: null,
      campaignMedium: null,
      campaignName: null,
    };
    this.#controller.accountGenerationChanged();
  }

  dispose(): void {
    this.#candidates.clear();
    this.#controller.dispose();
  }
}

export function createBrowserCapture(options: CreateBrowserCaptureOptions): BrowserCapture {
  return new BrowserCaptureImpl(options);
}
